import Cookies from 'js-cookie'
import { generateID } from './utils'

class StorageValue {
  constructor(key, expires = 0, aliasKey) {
    this.key = key
    this.expires = expires
    this.aliasKey = aliasKey
  }

  get(delimiter = '') {
    let value = Cookies.get(this.key) || null
    if (!value) {
      const fallback =
        sessionStorage.getItem(this.key) ||
        (this.aliasKey && Cookies.get(this.aliasKey)) ||
        null
      if (fallback) {
        value = this.set(fallback)
      }
    }
    if (typeof value === 'string' && delimiter) value = value.split(delimiter)
    return value
  }

  set(value, expires = this.expires) {
    if (!value) {
      Cookies.remove(this.key)
      if (this.aliasKey) Cookies.remove(this.aliasKey)
      sessionStorage.removeItem(this.key)
    } else {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
      const options = expires ? { expires } : {}
      Cookies.set(this.key, stringValue, options)
      if (this.aliasKey) Cookies.set(this.aliasKey, stringValue, options)
      sessionStorage.setItem(this.key, stringValue)
    }
    return value
  }
}

class StorageID extends StorageValue {
  constructor(key, expires = 0, aliasKey) {
    super(key, expires, aliasKey)
    this.get()
  }

  get() {
    let id = super.get()
    if (!id) {
      id = this.regenerate()
    }
    return id
  }

  regenerate() {
    return super.set(this.generate())
  }

  generate() {
    return generateID()
  }
}

class Session {
  static KEY_DEFINITIONS = {
    pageLoadId: {
      id: 'athosPageLoadId',
      aliasId: 'ssPageLoadId',
      expires: 0,
      generateIfEmpty: true,
      regenerateOnLoad: true
    },
    shopper: { id: 'athosShopperId', expires: 365 },
    sessionId: {
      id: 'athosSessionId',
      aliasId: 'ssSessionIdNamespace',
      expires: 0,
      generateIfEmpty: true
    },
    userId: {
      id: 'athosUserId',
      aliasId: 'ssUserId',
      expires: 365,
      generateIfEmpty: true
    },
    viewedProducts: { id: 'athosViewedProducts', expires: 365 * 7, delimiter: ',' },
    columnPreferences: { id: 'athosColumnPreferences', expires: 0 },
    omneoAuthToken: { id: 'omneoAuthToken', expires: 0 },
    omneoAuthExpiry: { id: 'omneoAuthExpiry', expires: 0 },
    walletPass: { id: 'walletPassUrl', expires: 0 },
    pwpMute: { id: 'pwpMute', expires: 0 }
  }

  constructor() {
    this.initializeStorageItems()
    this.setShopperID()
    this.initEventListeners()
  }

  initializeStorageItems() {
    for (const [key, value] of Object.entries(Session.KEY_DEFINITIONS)) {
      const StorageClass = value.generateIfEmpty ? StorageID : StorageValue
      this[key] = new StorageClass(value.id, value.expires, value.aliasId)
      if (value.regenerateOnLoad) {
        this[key].regenerate()
      } else if (value.generateIfEmpty) {
        this[key].get()
      }
    }
  }

  setShopperID() {
    const customerID = window.GM_STATE?.customer?.id
    if (customerID) {
      this.shopper.set(customerID)
    } else {
      this.shopper.set()
    }
  }

  initEventListeners() {
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        this.pageLoadId.regenerate()
      }
    })
  }
}

export const session = new Session()
